import { PlusIcon } from "@heroicons/react/solid"
import { Breadcrumbs } from "components/breadcrumbs"
import Button from "components/button"
import { Link } from "react-router-dom"
import { Filter, PickupAccountCard, PickupAccountTable } from "./components"
import { useTable, useUpdate } from "@refinedev/core"
import { useMemo } from "react"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import { IPickupAccountItem, PickupAccountType } from "interfaces"
import toast from "react-hot-toast"
import { isHaveActionPermission } from "utills/helpers"

export const PickupAccountList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IPickupAccountItem>({
    resource: `pickup/accounts`,
    syncWithLocation: true,
    filters: {
      initial: [
        {
          field: "account_type",
          operator: "eq",
          value: "pos",
        }
      ]
    },
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "data",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      account_type: logicalFilters.find((item) => item.field === "account_type")?.value || "",
    };
  }, [filters]);

  const _toggleActive = (id: string, accountType: PickupAccountType, isActive: boolean) => {
    if (window.confirm(`Are you sure you want to ${!isActive ? "activate": "deactivate"} this account?`)) {
      updateMutation({
        resource: "pickup/accounts",
        id: `${accountType.toLocaleLowerCase()}/${id}`,
        meta: {
          URLSuffix: !isActive ? "active" : "inactive",
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission("accounts", "pickup", "create") && {
          additionalLabel: (
            <Link to={"/pickup/accounts/create"}>
              <Button>
                <span className="sr-only">Create new account</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Create New Account
                </span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <PickupAccountCard
                data={tableQuery?.data?.data}
                accountType={currentFilterValues.account_type ?? ""}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <PickupAccountTable
                data={tableQuery?.data?.data}
                accountType={currentFilterValues.account_type ?? ""}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_items ?? 0,
                total: tableQuery?.data?.paging.total_qas
              }}
              onChange={_handlePageChanged}
              hideTotal
            />
          </div>
        ) : <></>}
      </div>
      {(tableQuery.isLoading) || (tableQuery.isFetching && tableQuery.isPreviousData) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}